import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Send, CheckCircle2, PenLine, Upload } from "lucide-react";
import Navbar from "@/components/Navbar";
import BulkSubmit from "@/components/BulkSubmit";
import { useCategories } from "@/context/CategoriesContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  title: "", sport: "", team: "", venue: "", start_at: "", end_at: "",
  description: "", ticket_url: "", submitter_name: "", submitter_email: "",
};

export default function Submit() {
  const { city } = useParams();
  const { sports: allCats, teams: allOrgs } = useCategories();
  const [mode, setMode] = useState("single"); // single | bulk
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const teams = useMemo(
    () => (form.sport ? allOrgs.filter((t) => t.sport === form.sport) : []),
    [form.sport, allOrgs]
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.sport || !form.venue || !form.start_at) {
      toast.error("Please fill in title, sport, venue and start time");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submit", {
        ...form,
        city,
        start_at: new Date(form.start_at).toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        team: form.team || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <CheckCircle2 className="text-ec-volt mx-auto mb-6" size={56} />
          <h1 className="font-heading text-5xl tracking-tight">Submitted!</h1>
          <p className="text-zinc-400 mt-4">
            Thanks — your event is now in the review queue. Once an admin approves it, it'll appear on
            the public calendar.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to={`/${city}`} className="overline bg-ec-volt text-black px-6 py-3.5">Back to Calendar</Link>
            <button onClick={() => { setForm(empty); setDone(false); }} className="overline border border-white/15 px-6 py-3.5 hover:border-ec-volt hover:text-ec-volt transition-colors">
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
        <p className="overline text-ec-volt mb-3">Community Submission</p>
        <h1 className="font-heading text-5xl sm:text-6xl tracking-tight leading-[0.85]">Submit an Event</h1>
        <p className="text-zinc-400 mt-4 mb-6">
          Know an event we've missed? Add one below or bulk-import a CSV. All submissions are reviewed
          by an admin before going live.
        </p>

        <div className="flex border border-white/15 mb-8 w-max" data-testid="submit-mode-toggle">
          <button data-testid="submit-mode-single" onClick={() => setMode("single")} className={`inline-flex items-center gap-2 overline px-4 py-2.5 transition-colors ${mode === "single" ? "bg-ec-volt text-black" : "text-zinc-300 hover:text-white"}`}>
            <PenLine size={15} /> Single Event
          </button>
          <button data-testid="submit-mode-bulk" onClick={() => setMode("bulk")} className={`inline-flex items-center gap-2 overline px-4 py-2.5 border-l border-white/15 transition-colors ${mode === "bulk" ? "bg-ec-volt text-black" : "text-zinc-300 hover:text-white"}`}>
            <Upload size={15} /> Bulk Import
          </button>
        </div>

        {mode === "bulk" ? (
          <BulkSubmit showSubmitter onDone={() => setDone(true)} />
        ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="submit-form">
          <Field label="Event Title *">
            <input data-testid="submit-title" value={form.title} onChange={set("title")} className={inputCls} placeholder="Worcester Warriors vs ..." />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Category *">
              <select data-testid="submit-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, team: "" })} className={inputCls}>
                <option value="">Select sport</option>
                {allCats.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Team / Venue">
              <select data-testid="submit-team" value={form.team} onChange={set("team")} className={inputCls} disabled={!form.sport}>
                <option value="">{form.sport ? "Select team (optional)" : "Pick a sport first"}</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Venue *">
            <input data-testid="submit-venue" value={form.venue} onChange={set("venue")} className={inputCls} placeholder="Sixways Stadium" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Start *">
              <input data-testid="submit-start" type="datetime-local" value={form.start_at} onChange={set("start_at")} className={inputCls} />
            </Field>
            <Field label="End">
              <input data-testid="submit-end" type="datetime-local" value={form.end_at} onChange={set("end_at")} className={inputCls} />
            </Field>
          </div>

          <Field label="Description">
            <textarea data-testid="submit-description" value={form.description} onChange={set("description")} rows={3} className={inputCls} placeholder="Anything else worth knowing..." />
          </Field>

          <Field label="Ticket / Info Link">
            <input data-testid="submit-ticket" type="url" value={form.ticket_url} onChange={set("ticket_url")} className={inputCls} placeholder="https://… where people can buy tickets / read more" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your Name">
              <input data-testid="submit-name" value={form.submitter_name} onChange={set("submitter_name")} className={inputCls} />
            </Field>
            <Field label="Your Email">
              <input data-testid="submit-email" type="email" value={form.submitter_email} onChange={set("submitter_email")} className={inputCls} />
            </Field>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-btn"
            className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-7 py-3.5 hover:-translate-y-0.5 hover:shadow-[0_0_18px_rgba(204,255,0,0.4)] transition-all disabled:opacity-50"
          >
            <Send size={16} /> {submitting ? "Submitting…" : "Submit for Review"}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-zinc-500 mb-2 block">{label}</span>
      {children}
    </label>
  );
}
